import { useLazyQuery, useMutation } from '@apollo/client';
import type { FetchResult } from '@apollo/client';
import { Button, Callout, Spinner, Text, TextField } from '@radix-ui/themes';
import isDeepEqual from 'fast-deep-equal/react';
import { Form } from 'radix-ui';
import { use, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { firebaseApp } from '../../../auth/FirebaseApp';
import { config } from '../../../config';
import { LOGIN, LOGIN_SETTINGS } from '../../../gql';
import { i18n } from '../../../i18n';
import { fetchAccessToken, setAccessToken } from '../../accesstoken';
import Title from '../../elements/Title';
import LocaleContext from '../../hooks/LocaleContext';
import BackToHubert from '../../navbar/BackToHubert';
import { consumeSignOutReason, setExpectedUserId } from '../../sessionIdentity';

type SubmitData = {
  email: string;
  password?: string;
};

const consumedSignOutReason = consumeSignOutReason();

const initialzeSso = async (pid: string) => {
  const settings = await (await fetch(`${config.VITE_GRAPHQL_URL}/api/sso/${pid}`)).json();
  const firebase = await firebaseApp.initialize({ authDomain: settings.authDomain });
  return {
    signInWithPopup() {
      return firebase.signInWithPopup(pid);
    },
    signInWithRedirect() {
      return firebase.signInWithRedirect(pid);
    },
    getRedirectResult() {
      return firebase.getRedirectResult();
    },
  };
};

const getProviderParams = async (pid: string, email: string, password: string) => {
  if (pid && pid !== 'password') {
    const sso = await initialzeSso(pid);
    const r = await sso.signInWithPopup();

    return {
      email: r.user.email,
      password: await r.user.getIdToken(),
      provider: pid,
    };
  }
  return { email, password, provider: undefined };
};

const appendQuery = (q: string, append: string) => (q.startsWith('?') ? `${q}&${append}` : `?${append}`);

const SignIn = ({ pid, auto }: { pid?: string | null; auto?: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [loginProvider, setLoginProvider] = useState<{ provider: string; authDomain?: string } | null>(null);
  const [isFederatedLogin, setFederatedLogin] = useState(false);

  pid ??= loginProvider ? loginProvider.provider : query.get('pid');
  if (loginProvider == null && pid) setLoginProvider({ provider: pid });
  auto ||= !!(query.get('auto') ?? false);

  if (!auto && loginProvider && loginProvider.authDomain === window.location.host)
    navigate(
      { pathname: location.pathname, search: appendQuery(location.search, `pid=${loginProvider.provider}&auto=1`) },
      { replace: true },
    );

  const redirectTo = query.get('redirect');

  useEffect(() => {
    if (!loginProvider && redirectTo) {
      (async () => {
        const settings = await (await fetch(`${config.VITE_GRAPHQL_URL}/api/sso/redirect?to=${redirectTo}`)).json();
        if (settings.ok && settings.provider !== 'password') setLoginProvider(settings);
      })();
    }
    setFederatedLogin(loginProvider ? loginProvider.provider !== 'password' : false);
  }, [loginProvider]);

  const loginWithRedirect = async () => {
    if (!pid) throw new Error('Missing provider id');
    const sso = await initialzeSso(pid);
    try {
      const res = await sso.getRedirectResult();
      if (res != null) {
        const variables = {
          email: res.user.email,
          password: await res.user.getIdToken(),
          provider: pid,
        };
        login({
          variables,
        })
          .then((result) => handleLoginResponse(result, variables.email ?? ''))
          .catch(onLoginError);
      } else if (!loading) {
        await sso.signInWithRedirect();
      }
    } catch (err) {
      if (err instanceof Error) onLoginError(err);
      else onLoginError({ message: t('signIn.error.redirectFailed') });
    }
  };

  useEffect(() => {
    if (isFederatedLogin && auto) {
      if (!loginError) loginWithRedirect();
    }
  }, [isFederatedLogin]);

  const { userSettings, setLoggedIn } = use(LocaleContext);
  const prevParams = useRef(navigate);

  const [loginError, setLoginError] = useState<ReactNode>();
  const sessionEndedMessage =
    query.get('reason') === 'session-ended' || consumedSignOutReason === 'session-ended'
      ? t('signIn.error.sessionEnded')
      : undefined;
  const displayError = loginError ?? sessionEndedMessage;

  useEffect(() => {
    if (!isDeepEqual(prevParams.current, navigate)) {
      if (userSettings.loggedIn) {
        if (redirectTo) navigate(redirectTo);
        else navigate('/');
      }
      prevParams.current = navigate;
    }
  }, [userSettings, history, query]);

  const [login, { loading }] = useMutation(LOGIN, {
    onCompleted() {
      if (loading) console.log('Loading…');
    },
  });

  const [loginSettings] = useLazyQuery(LOGIN_SETTINGS);

  const onLoginError = (err: Parameters<typeof errorSignOn>[0]) => {
    console.error(err);
    errorSignOn(err);
  };

  const handleLoginResponse = async (
    response: FetchResult<{ login?: { __typename: string; accessToken: string } }>,
    email: string,
  ) => {
    if (response && response.data && response.data.login) {
      const typename = response.data.login.__typename;

      if (typename === 'LoginResponse') {
        setAccessToken(response.data.login.accessToken);
        setExpectedUserId(response.data.login.accessToken);
        setLoggedIn?.(true);
        setLoginError(undefined);
        await fetchAccessToken();
        const redirect = query.get('redirect');
        if (redirect) navigate(redirect);
        else navigate('/jobs');
      } else if (typename === 'UserNotActivated') {
        setLoginError(
          <div>
            <div>{t('signIn.activation.message')}</div>
            <NavLink to="/forgot" state={{ email, state: 'not-activated' }}>
              <Button type="button" size="2" variant="soft" color="gray" className="mt-2">
                {t('signIn.activation.requestNewButton')}
              </Button>
            </NavLink>
          </div>,
        );
      } else if (typename === 'LoginError') {
        setLoginError(t('signIn.error.invalidCredentials'));
      }
    }
  };

  const handleLogin = async (loginProvider: string, email: string, password: string) => {
    const providerParams = await getProviderParams(loginProvider, email, password);
    try {
      const loginResponse = await login({ variables: providerParams });
      await handleLoginResponse(loginResponse, email);
    } catch (err) {
      if (err instanceof Error) onLoginError(err);
    }
  };

  const errorSignOn = (error: { message: string; networkError?: { result?: { errors: { message: string }[] } } }) => {
    console.log('errorSignOn error', error);
    const errors = error.networkError?.result?.errors;
    const message = (errors && errors[0]?.message) ?? error.message;
    setLoggedIn?.(false);
    setLoginError(message);
  };

  if (auto)
    return SignInFrame(
      <>
        {displayError && (
          <Callout.Root color="red">
            <Callout.Text className="mb-0">{displayError}</Callout.Text>
          </Callout.Root>
        )}
        {loading ? (
          <div className="flex justify-center">
            <Spinner size="3" role="status" aria-hidden="true" />
          </div>
        ) : (
          <></>
        )}
      </>,
      { heading: t('signIn.autoHeading') },
    );

  return SignInFrame(
    <>
      {displayError && (
        <Callout.Root color="red" className="mb-4">
          <Callout.Text className="mb-0">{displayError}</Callout.Text>
        </Callout.Root>
      )}
      <Form.Root
        onSubmit={(formEvent) => {
          formEvent.preventDefault();

          const form = formEvent.currentTarget;
          const formData = Object.fromEntries(new FormData(form)) as unknown as SubmitData;

          if (loginProvider) {
            handleLogin(
              loginProvider.provider ?? '',
              formData.email?.trim().toLowerCase() ?? '',
              formData.password?.trim() ?? '',
            );
          } else {
            loginSettings({ variables: { email: formData.email.trim().toLowerCase() } }).then((value) => {
              setLoginProvider(value.data.loginSettings);
            });
          }
        }}
        className="w-full flex flex-col items-center justify-center gap-4"
      >
        {isFederatedLogin ? null : (
          <Form.Field name="email" className="w-full">
            <div className="flex items-baseline justify-center">
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('signIn.form.emailRequired')}</Form.Message>
                <Form.Message match="typeMismatch">{t('signIn.form.emailInvalid')}</Form.Message>
              </Text>
            </div>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('signIn.form.emailPlaceholder')}
                type="email"
                className="w-full text-center!"
                size="3"
                required={!isFederatedLogin}
                readOnly={loginProvider ? true : false}
              />
            </Form.Control>
          </Form.Field>
        )}
        {!loginProvider || isFederatedLogin ? null : (
          <Form.Field name="password" className="w-full">
            <div className="flex items-baseline justify-center">
              <Text size="1" color="red">
                <Form.Message match="valueMissing">{t('signIn.form.passwordRequired')}</Form.Message>
              </Text>
            </div>
            <Form.Control asChild>
              <TextField.Root
                placeholder={t('signIn.form.passwordPlaceholder')}
                type="password"
                className="w-full text-center!"
                size="3"
                required={!isFederatedLogin}
              />
            </Form.Control>
          </Form.Field>
        )}
        <Form.Submit asChild>
          <Button size="3" className="w-full!" loading={loading}>
            {loginProvider ? t('signIn.button.signIn') : t('signIn.button.next')}
          </Button>
        </Form.Submit>
        {loginProvider && (
          <div className="flex items-baseline justify-center">
            <a style={{ fontSize: '80%', color: 'grey' }} href=".">
              {t('signIn.cancel')}
            </a>
          </div>
        )}
        <NavLink to="/forgot">
          <Button type="button" size="2" variant="soft" color="gray" className="w-full">
            {t('signIn.troubleSigningIn')}
          </Button>
        </NavLink>
      </Form.Root>
    </>,
  );
};

const SignInFrame = (x: ReactNode, options?: { heading: string }) => (
  <div className="h-screen flex flex-col items-center justify-center">
    <Title title={i18n.t('signIn.pageTitle')} />
    <BackToHubert />
    <div className="w-3/4 sign-in" style={{ maxWidth: '25rem' }}>
      <div className="text-center">
        <img src="/logo/hubert_logo_lockup_dark@2x.png" className="w-full" alt={i18n.t('signIn.logo.alt')} />
        <h2 style={{ fontSize: '1.9rem' }}>{options?.heading ?? i18n.t('signIn.defaultHeading')}</h2>
      </div>
      {x}
    </div>
  </div>
);

export default SignIn;
